import { signal } from "@preact/signals";
import { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import hljs from "highlight.js/lib/core";
import hljsTs from "highlight.js/lib/languages/typescript";
import hljsJson from "highlight.js/lib/languages/json";
import hljsBash from "highlight.js/lib/languages/bash";
import "highlight.js/styles/github.css";
import {
  codeChildProps,
  CopyButton,
  inlineCodeClass,
  Spinner,
} from "./components.tsx";
import { usePageMeta } from "./pageMeta.ts";

hljs.registerLanguage("typescript", hljsTs);
hljs.registerLanguage("json", hljsJson);
hljs.registerLanguage("bash", hljsBash);

const darkCodeTheme = `.dark .hljs { color: #c9d1d9; background: #0d1117 }
  .dark .hljs-doctag, .dark .hljs-keyword, .dark .hljs-meta .hljs-keyword,
  .dark .hljs-template-tag, .dark .hljs-template-variable, .dark .hljs-type,
  .dark .hljs-variable.language_ { color: #ff7b72 }
  .dark .hljs-title, .dark .hljs-title.class_, .dark .hljs-title.class_.inherited__,
  .dark .hljs-title.function_ { color: #d2a8ff }
  .dark .hljs-attr, .dark .hljs-attribute, .dark .hljs-literal, .dark .hljs-meta,
  .dark .hljs-number, .dark .hljs-operator, .dark .hljs-variable, .dark .hljs-selector-attr,
  .dark .hljs-selector-class, .dark .hljs-selector-id { color: #79c0ff }
  .dark .hljs-regexp, .dark .hljs-string, .dark .hljs-meta .hljs-string { color: #a5d6ff }
  .dark .hljs-built_in, .dark .hljs-symbol { color: #ffa657 }
  .dark .hljs-comment, .dark .hljs-code, .dark .hljs-formula { color: #8b949e }
  .dark .hljs-name, .dark .hljs-quote, .dark .hljs-selector-tag,
  .dark .hljs-selector-pseudo { color: #7ee787 }
  .dark .hljs-subst { color: #c9d1d9 }
  .dark .hljs-section { color: #1f6feb; font-weight: bold }
  .dark .hljs-bullet { color: #f2cc60 }
  .dark .hljs-addition { color: #aff5b4; background-color: #033a16 }
  .dark .hljs-deletion { color: #ffdcd7; background-color: #67060c }`;

if (typeof document !== "undefined") {
  const darkStyleEl = document.createElement("style");
  darkStyleEl.textContent = darkCodeTheme;
  document.head.appendChild(darkStyleEl);
}

type NavItem = { id: string; label: string };
type NavGroup = { group: string; items: NavItem[] };

const sidebarNav: NavGroup[] = [
  {
    group: "Getting Started",
    items: [
      { id: "overview", label: "Overview" },
      { id: "client-library", label: "Client Library" },
    ],
  },
  {
    group: "Concepts",
    items: [
      { id: "auth", label: "Authentication & Signing" },
      { id: "custom-tools", label: "Custom Tools & Skills" },
      { id: "deferred-tools", label: "Deferred (Async) Tools" },
      { id: "recorder-webhook", label: "Recorder Webhook" },
    ],
  },
  {
    group: "API Endpoints",
    items: [
      { id: "set-custom-tools", label: "set-custom-tools" },
      { id: "set-prompt", label: "set-prompt" },
      { id: "create-remote-task", label: "create-remote-task" },
      { id: "list-scheduled-tasks", label: "list-scheduled-tasks" },
      { id: "delete-scheduled-task", label: "delete-scheduled-task" },
      { id: "inject-context", label: "inject-context" },
      { id: "pause-interaction", label: "pause-interaction" },
      { id: "ui-update", label: "Spinner & Progress Bar" },
      { id: "create-bot-api", label: "create-bot-api" },
      { id: "list-bots", label: "list-bots" },
      { id: "delete-bot-api", label: "delete-bot-api" },
      { id: "create-guest", label: "create-guest" },
      { id: "login-with-token", label: "login-with-token" },
    ],
  },
  {
    group: "Guides",
    items: [
      { id: "third-party-integration", label: "3rd Party Integration" },
    ],
  },
  {
    group: "Reference",
    items: [
      { id: "alice-and-bot", label: "Alice & Bot" },
      { id: "abilities", label: "Abilities" },
    ],
  },
];

const allSectionIds = sidebarNav.flatMap(({ items }) =>
  items.map(({ id }) => id)
);

// Map heading text to sidebar section IDs
const headingIdMap: Record<string, string> = {
  "API Reference": "overview",
  "TypeScript Client Library": "client-library",
  "Authentication & Signing": "auth",
  "Webhook Signing": "auth",
  "Custom Tools & Skills": "custom-tools",
  "Deferred (Async) Tools": "deferred-tools",
  "Conversation Recorder Webhook": "recorder-webhook",
  "set-custom-tools": "set-custom-tools",
  "set-prompt": "set-prompt",
  "create-remote-task": "create-remote-task",
  "inject-context": "inject-context",
  "pause-interaction / pauseInteraction": "pause-interaction",
  "pause-interaction": "pause-interaction",
  "Spinner & Progress Bar": "ui-update",
  "create-bot-api": "create-bot-api",
  "list-bots": "list-bots",
  "delete-bot-api": "delete-bot-api",
  "create-guest": "create-guest",
  "login-with-token": "login-with-token",
  "3rd Party Integration": "third-party-integration",
  "Alice & Bot (Chat Transport)": "alice-and-bot",
  "Abilities": "abilities",
};

const slugify = (text: string): string =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const headingId = (text: string): string => headingIdMap[text] ?? slugify(text);

const extractText = (children: ComponentChildren): string => {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(extractText).join("");
  if (children && typeof children === "object" && "props" in children) {
    return extractText(
      (children as { props: { children?: ComponentChildren } }).props.children,
    );
  }
  return "";
};

const useActiveSection = (sectionIds: string[], content: string) => {
  const activeRef = useRef("overview");
  const activeSignal = signal("overview");
  useEffect(() => {
    if (!content || typeof IntersectionObserver === "undefined") return;
    const observerMap = new Map<string, boolean>();
    sectionIds.forEach((id) => observerMap.set(id, false));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          observerMap.set(entry.target.id, entry.isIntersecting);
        });
        const first = sectionIds.find((id) => observerMap.get(id));
        if (first && first !== activeRef.current) {
          activeRef.current = first;
          activeSignal.value = first;
          if (typeof history !== "undefined" && history.replaceState) {
            history.replaceState(null, "", `#${first}`);
          }
        }
      },
      { rootMargin: "-80px 0px -50% 0px", threshold: 0 },
    );

    const rafId = requestAnimationFrame(() => {
      sectionIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    });

    const handleScroll = () => {
      if (
        globalThis.innerHeight + globalThis.scrollY >=
          document.documentElement.scrollHeight - 50
      ) {
        const lastId = [...sectionIds].reverse().find((id) =>
          document.getElementById(id)
        );
        if (lastId && lastId !== activeRef.current) {
          activeRef.current = lastId;
          activeSignal.value = lastId;
          if (typeof history !== "undefined" && history.replaceState) {
            history.replaceState(null, "", `#${lastId}`);
          }
        }
      }
    };

    globalThis.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      globalThis.removeEventListener("scroll", handleScroll);
    };
  }, [sectionIds, content]);
  return activeSignal;
};

const sidebarMobileOpen = signal(false);

const sidebarLinkBase =
  "block py-1.5 px-3 text-sm rounded-md transition-colors truncate text-left";

const sidebarLinkActive =
  `${sidebarLinkBase} text-accent-light dark:text-accent bg-accent/8 font-medium border-l-2 border-accent`;

const sidebarLinkInactive =
  `${sidebarLinkBase} text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary hover:bg-surface-overlay-light dark:hover:bg-surface-overlay border-l-2 border-transparent`;

const sidebarGroupLabel =
  "text-xs font-semibold uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary px-3 mb-1";

const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    history.replaceState(null, "", `#${id}`);
  }
  sidebarMobileOpen.value = false;
};

const SidebarContent = (
  { active }: { active: { value: string } },
) => (
  <nav className="flex flex-col gap-4">
    {sidebarNav.map(({ group, items }) => (
      <div key={group}>
        <div className={sidebarGroupLabel}>{group}</div>
        <div className="flex flex-col gap-0.5">
          {items.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={active.value === id
                ? sidebarLinkActive
                : sidebarLinkInactive}
              onClick={() => scrollToSection(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    ))}
  </nav>
);

const MobileSidebarToggle = () => (
  <button
    type="button"
    className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised text-text-primary-light dark:text-text-primary hover:border-accent/50 transition-colors cursor-pointer"
    onClick={() => sidebarMobileOpen.value = !sidebarMobileOpen.value}
    aria-label="Toggle docs navigation"
  >
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
    Navigation
  </button>
);

const MobileSidebarOverlay = (
  { active }: { active: { value: string } },
) => {
  if (!sidebarMobileOpen.value) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div
        className="fixed inset-0 bg-black/40"
        onClick={() => sidebarMobileOpen.value = false}
      />
      <div className="relative z-50 w-72 max-w-[80vw] bg-surface-light dark:bg-surface border-r border-border-subtle-light dark:border-border-subtle p-4 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-semibold text-text-primary-light dark:text-text-primary">
            Documentation
          </span>
          <button
            type="button"
            className="p-1 rounded-md hover:bg-surface-overlay-light dark:hover:bg-surface-overlay transition-colors cursor-pointer"
            onClick={() => sidebarMobileOpen.value = false}
            aria-label="Close navigation"
          >
            <svg
              className="w-5 h-5 text-text-secondary-light dark:text-text-secondary"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <SidebarContent active={active} />
      </div>
    </div>
  );
};

const prose =
  "text-sm leading-relaxed text-text-secondary-light dark:text-text-secondary";

const sectionClass =
  "border border-border-subtle-light dark:border-border-subtle rounded-lg p-6 bg-surface-raised-light dark:bg-surface";

const isSectionHeading = (text: string): boolean => text in headingIdMap;

const hljsLang = (lang: string) => hljs.getLanguage(lang) ? lang : undefined;

const HighlightedCode = (
  { code, lang }: { code: string; lang?: string },
) => {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    const resolved = lang ? hljsLang(lang) : undefined;
    ref.current.innerHTML = resolved
      ? hljs.highlight(code.trim(), { language: resolved }).value
      : code.trim().replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(
        />/g,
        "&gt;",
      );
  }, [code, lang]);
  return (
    <div className="relative group">
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition">
        <CopyButton text={code.trim()} />
      </div>
      {lang && (
        <div className="text-[10px] uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary mb-1">
          {lang}
        </div>
      )}
      <pre className="overflow-auto text-xs p-4 bg-surface-light dark:bg-surface-raised rounded-lg border border-border-subtle-light dark:border-border-subtle font-mono leading-5">
        <code ref={ref} />
      </pre>
    </div>
  );
};

const markdownComponents = {
  h1: ({ children }: { children?: ComponentChildren }) => {
    const text = extractText(children);
    const id = headingId(text);
    return (
      <div id={id} className="scroll-mt-20">
        <h1 className="text-3xl font-bold text-text-primary-light dark:text-text-primary">
          {children}
        </h1>
      </div>
    );
  },
  h2: ({ children }: { children?: ComponentChildren }) => {
    const text = extractText(children);
    const id = headingId(text);
    const isSection = isSectionHeading(text);
    return isSection
      ? (
        <div
          id={id}
          className={`${sectionClass} scroll-mt-20 mt-8 first:mt-0`}
        >
          <h2 className="text-2xl font-display font-bold text-text-primary-light dark:text-text-primary mb-4">
            {children}
          </h2>
        </div>
      )
      : (
        <h2
          id={id}
          className="text-2xl font-display font-bold text-text-primary-light dark:text-text-primary mb-4 mt-8 scroll-mt-20"
        >
          {children}
        </h2>
      );
  },
  h3: ({ children }: { children?: ComponentChildren }) => {
    const text = extractText(children);
    const id = headingId(text);
    return (
      <h3
        id={id}
        className="text-lg font-display font-semibold text-text-primary-light dark:text-text-primary mb-2 mt-6 scroll-mt-20"
      >
        {children}
      </h3>
    );
  },
  h4: ({ children }: { children?: ComponentChildren }) => (
    <h4 className="text-sm font-semibold text-text-secondary-light dark:text-text-secondary mb-2 mt-4">
      {children}
    </h4>
  ),
  p: ({ children }: { children?: ComponentChildren }) => (
    <p className={`${prose} mb-3`}>{children}</p>
  ),
  a: (
    { href, children }: { href?: string; children?: ComponentChildren },
  ) => (
    <a
      href={href}
      className="text-accent-light dark:text-accent hover:underline"
      {...(href?.startsWith("http")
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
    >
      {children}
    </a>
  ),
  strong: ({ children }: { children?: ComponentChildren }) => (
    <strong className="font-semibold text-text-primary-light dark:text-text-primary">
      {children}
    </strong>
  ),
  blockquote: ({ children }: { children?: ComponentChildren }) => (
    <blockquote className="mb-4 p-3 bg-accent/5 border border-accent/20 rounded text-sm text-accent-light dark:text-accent">
      {children}
    </blockquote>
  ),
  ul: ({ children }: { children?: ComponentChildren }) => (
    <ul className="list-disc pl-6 mb-4 space-y-1 text-sm text-text-secondary-light dark:text-text-secondary">
      {children}
    </ul>
  ),
  ol: ({ children }: { children?: ComponentChildren }) => (
    <ol className="list-decimal pl-6 mb-4 space-y-1 text-sm text-text-secondary-light dark:text-text-secondary">
      {children}
    </ol>
  ),
  li: ({ children }: { children?: ComponentChildren }) => <li>{children}</li>,
  pre: ({ children }: { children?: ComponentChildren }) => {
    const { className = "", children: code = "" } = codeChildProps(children);
    const lang = className.replace("language-", "");
    return <HighlightedCode code={String(code)} lang={lang || undefined} />;
  },
  code: ({ children }: { children?: ComponentChildren }) => (
    <code className={inlineCodeClass}>{children}</code>
  ),
  table: ({ children }: { children?: ComponentChildren }) => (
    <div className="overflow-x-auto mb-4">
      <table className="w-full text-left text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: ComponentChildren }) => (
    <thead>{children}</thead>
  ),
  tbody: ({ children }: { children?: ComponentChildren }) => (
    <tbody>{children}</tbody>
  ),
  tr: ({ children }: { children?: ComponentChildren }) => (
    <tr className="border-b border-border-subtle-light dark:border-border-subtle">
      {children}
    </tr>
  ),
  th: ({ children }: { children?: ComponentChildren }) => (
    <th className="py-2 pr-3 font-medium text-xs uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary">
      {children}
    </th>
  ),
  td: ({ children }: { children?: ComponentChildren }) => (
    <td className="py-2 pr-3 align-top text-xs text-text-secondary-light dark:text-text-secondary">
      {children}
    </td>
  ),
};

export const ApiDocs = () => {
  usePageMeta({
    title: "API Docs | prompt2bot",
    description: "Reference documentation for the prompt2bot API and SDK.",
  });
  const [content, setContent] = useState("");
  const active = useActiveSection(allSectionIds, content);
  useEffect(() => {
    fetch("/docs.md")
      .then((r) => r.text())
      .then(setContent)
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!content) return;
    const hash = globalThis.location?.hash?.slice(1);
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ block: "start" });
  }, [content]);
  return (
    <div className="flex-grow">
      <div className="md:hidden mb-4 px-4">
        <MobileSidebarToggle />
        <MobileSidebarOverlay active={active} />
      </div>
      <div className="flex gap-8 pb-16 px-4">
        <aside
          className="hidden md:block sticky top-16 self-start w-56 shrink-0 overflow-y-auto pr-2"
          style={{ maxHeight: "calc(100dvh - 5rem)" }}
        >
          <SidebarContent active={active} />
        </aside>
        <div className="max-w-4xl flex flex-col gap-2 flex-grow min-w-0">
          {content
            ? (
              <Markdown
                remarkPlugins={[remarkGfm]}
                // @ts-expect-error: Preact VNode vs React ReactNode mismatch
                components={markdownComponents}
              >
                {content}
              </Markdown>
            )
            : (
              <div className="flex flex-1 justify-center items-center py-20 w-full h-full">
                <Spinner className="h-10 w-10 text-accent" />
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
